/**
 * G6.1 — QueueDock: the left rail beside the dispatch board.
 *
 * Two tabs with live badge counts:
 *   • "Return needed" — content/logic relocated from the old full-width
 *     ReturnNeededStrip (field directive 2026-07-14). G6.1-polish cards:
 *     clickable (opens the job detail page), customer · site headline,
 *     reason chip(s), the tech's note (2-line clamp, tooltip for the rest),
 *     device summary, right-chevron affordance. All three actions survive
 *     as icon buttons (Pick a time / Back to Awaiting Dispatch / Dismiss).
 *     Clearing stays server-derived: rescheduling past the flag hides the
 *     row.
 *   • "Awaiting" — the BacklogPanel (Awaiting Dispatch) rendered verbatim,
 *     with its by-city grouping, bulk assign, and drag-onto-tech wiring
 *     untouched. The CoverageQueueSection (call-in coverage) rides at the
 *     top of this tab — same drag protocol, renders nothing when empty.
 *
 * The dock collapses to a ~46px icon strip with badge counts; collapse
 * state + last tab persist in localStorage so the choice survives reloads.
 * Drag-and-drop onto tech lanes is untouched — the drag sources inside
 * BacklogPanel / CoverageQueueSection still write `text/plain = jobId`.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarPlus, ChevronRight, ChevronsLeft, ChevronsRight, Inbox, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { BacklogPanel } from "./backlog-panel";
import { CoverageQueueSection } from "./coverage-queue-section";
import type { Cluster } from "./demand-panel";
import type { DispatchJob, DispatchTech } from "./dispatch-types";

/* ─── Return-needed data (relocated from return-needed-strip.tsx) ────── */

export interface ReturnNeededFlag {
    id: string;
    job_id: string;
    asset_id: string | null;
    reason_category: string;
    reason_text: string | null;
    created_at: string;
    display_number: string | null;
    job_title: string | null;
    job_status: string;
    scheduled_start: string | null;
    assigned_to: string | null;
    customer_name: string | null;
    site_name: string | null;
    site_city: string | null;
    asset_name: string | null;
    asset_serial: string | null;
    flagged_by_name: string | null;
}

const REASON_LABEL: Record<string, string> = {
    no_access: "No access (keys/locked)",
    manager_unavailable: "Manager unavailable",
    water_shutoff: "Water can't be shut off",
    no_water: "No water to device",
    ran_out_of_time: "Out of time",
    parts_needed: "Need parts",
    unsafe: "Unsafe/blocked",
    other: "Other",
    cannot_complete: "Cannot complete",
};

/* ─── Collapse/tab persistence ────────────────────────────────────────── */

export type QueueDockTab = "return" | "awaiting";

interface QueueDockState {
    collapsed: boolean;
    tab: QueueDockTab;
}

const DOCK_STATE_KEY = "grid.queueDock.v1";

export function loadQueueDockState(): QueueDockState {
    try {
        const raw = window.localStorage.getItem(DOCK_STATE_KEY);
        if (!raw) return { collapsed: false, tab: "awaiting" };
        const parsed = JSON.parse(raw) as Partial<QueueDockState>;
        return {
            collapsed: parsed.collapsed === true,
            tab: parsed.tab === "return" ? "return" : "awaiting",
        };
    } catch {
        return { collapsed: false, tab: "awaiting" };
    }
}

export function saveQueueDockState(state: QueueDockState): void {
    try {
        window.localStorage.setItem(DOCK_STATE_KEY, JSON.stringify(state));
    } catch {
        // Storage full / privacy mode — the dock just won't remember.
    }
}

/* ─── Dock ────────────────────────────────────────────────────────────── */

export interface QueueDockProps {
    techs: DispatchTech[];
    /** Date used by the backlog's "bulk assign" action. */
    targetDate: string;
    /** BacklogPanel's collapsed (auto-scheduler summary) mode flag. */
    backlogCollapsed: boolean;
    /** #1766 — new Grid: readiness tags on each awaiting card. */
    showReadiness?: boolean;
    techSkills?: Map<string, string[]>;
    jobSkillReqs?: Map<string, string[]>;
    techCityCerts?: Map<string, Set<string>>;
    jobCityRestriction?: Map<string, { id: string; label: string }>;
    onJobClick?: (jobId: string) => void;
    onScheduleJob?: (jobId: string) => void;
    onJobDragStart?: (jobId: string) => void;
    onJobDragEnd?: () => void;
    onUnscheduleJob?: (jobId: string) => void;
    onCancelJob?: (jobId: string) => void;
    onAssignJob?: (jobId: string, techId: string) => void;
    /** "Pick a time now" on a return-needed row — opens Schedule & Crew. */
    onRescheduleReturn: (job: DispatchJob) => void;
}

function badgeText(n: number): string {
    return n > 99 ? "99+" : String(n);
}

export function QueueDock({
    techs,
    targetDate,
    backlogCollapsed,
    showReadiness = false,
    techSkills,
    jobSkillReqs,
    techCityCerts,
    jobCityRestriction,
    onJobClick,
    onScheduleJob,
    onJobDragStart,
    onJobDragEnd,
    onUnscheduleJob,
    onCancelJob,
    onAssignJob,
    onRescheduleReturn,
}: QueueDockProps) {
    const [{ collapsed, tab }, setDockState] = useState<QueueDockState>(() => loadQueueDockState());
    useEffect(() => {
        saveQueueDockState({ collapsed, tab });
    }, [collapsed, tab]);
    const setCollapsed = (next: boolean) => setDockState((s) => ({ ...s, collapsed: next }));
    const setTab = (next: QueueDockTab) => setDockState((s) => ({ ...s, tab: next }));

    const qc = useQueryClient();
    const flagsQ = useQuery({
        queryKey: ["dispatch", "return-needed"],
        queryFn: () => api.get<{ flags: ReturnNeededFlag[] }>("/api/dispatch/return-needed"),
        refetchInterval: 60_000,
    });

    // Badge count for the Awaiting tab. Same queryKey the shell and the
    // BacklogPanel already use, so this reads the shared cache — no extra
    // network traffic.
    const clustersQ = useQuery({
        queryKey: ["dispatch", "clusters"],
        queryFn: () => api.get<{ clusters: Cluster[]; totalJobs: number }>("/api/dispatch/clusters"),
    });

    const dismiss = useMutation({
        mutationFn: (flagId: string) => api.post(`/api/dispatch/return-needed/${flagId}/dismiss`, {}),
        onSuccess: () => {
            toast.success("Dismissed — logged");
            qc.invalidateQueries({ queryKey: ["dispatch", "return-needed"] });
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Dismiss failed"),
    });

    // Second exit: we often don't know where the return fits yet — one tap
    // parks the job in Awaiting Dispatch with its story (reason, remaining
    // devices, due-by) stamped on the card. Flags resolve + audit server-side.
    const backToQueue = useMutation({
        mutationFn: (jobId: string) =>
            api.post<{ ok: boolean; remainingDevices: Array<{ label: string }> }>(
                `/api/dispatch/return-needed/jobs/${jobId}/back-to-queue`,
                {},
            ),
        onSuccess: (res) => {
            const n = res.remainingDevices?.length ?? 0;
            toast.success(
                `Back in Awaiting Dispatch — ${n === 0 ? "return trip" : `${n} device${n === 1 ? "" : "s"} still outstanding`}.`,
            );
            qc.invalidateQueries({ queryKey: ["dispatch", "return-needed"] });
            qc.invalidateQueries({ queryKey: ["dispatch"] });
            qc.invalidateQueries({ queryKey: ["jobs"] });
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Couldn't send it back to the queue"),
    });

    // One row per job; devices/reasons collapse under it.
    const flags = flagsQ.data?.flags ?? [];
    const byJob = new Map<string, ReturnNeededFlag[]>();
    for (const f of flags) {
        const list = byJob.get(f.job_id) ?? [];
        list.push(f);
        byJob.set(f.job_id, list);
    }
    const returnCount = byJob.size;
    const awaitingCount = clustersQ.data?.totalJobs ?? 0;

    /* ── Collapsed: ~46px icon strip with badge counts ───────────────── */
    if (collapsed) {
        return (
            <aside data-testid="queue-dock" className="w-[46px] shrink-0 lg:sticky lg:top-0 lg:self-start">
                <div className="flex flex-col items-center gap-1 rounded-lg border bg-card p-1 shadow-sm">
                    <button
                        type="button"
                        title="Expand queues"
                        aria-label="Expand queues"
                        onClick={() => setCollapsed(false)}
                        className="flex h-8 w-8 items-center justify-center rounded-md text-foreground/70 transition-colors hover:bg-muted hover:text-foreground"
                    >
                        <ChevronsRight className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        title={`Return needed — ${returnCount}`}
                        aria-label={`Return needed — ${returnCount}`}
                        onClick={() => {
                            setDockState({ collapsed: false, tab: "return" });
                        }}
                        className="relative flex h-8 w-8 items-center justify-center rounded-md text-orange-700 transition-colors hover:bg-muted dark:text-orange-300"
                    >
                        <RotateCcw className="h-4 w-4" />
                        {returnCount > 0 && (
                            <span className="absolute -right-0.5 -top-0.5 rounded-full bg-orange-700 px-1 text-[9px] font-bold leading-3 text-white">
                                {badgeText(returnCount)}
                            </span>
                        )}
                    </button>
                    <button
                        type="button"
                        title={`Awaiting Dispatch — ${awaitingCount}`}
                        aria-label={`Awaiting Dispatch — ${awaitingCount}`}
                        onClick={() => {
                            setDockState({ collapsed: false, tab: "awaiting" });
                        }}
                        className="relative flex h-8 w-8 items-center justify-center rounded-md text-amber-700 transition-colors hover:bg-muted dark:text-amber-300"
                    >
                        <Inbox className="h-4 w-4" />
                        {awaitingCount > 0 && (
                            <span className="absolute -right-0.5 -top-0.5 rounded-full bg-amber-700 px-1 text-[9px] font-bold leading-3 text-white">
                                {badgeText(awaitingCount)}
                            </span>
                        )}
                    </button>
                </div>
            </aside>
        );
    }

    /* ── Expanded dock ────────────────────────────────────────────────── */
    return (
        <aside data-testid="queue-dock" className="w-full shrink-0 lg:sticky lg:top-0 lg:w-80 lg:self-start">
            <div className="flex max-h-[calc(100vh-8.5rem)] flex-col rounded-lg border bg-card shadow-sm">
                <div className="flex items-center gap-1 border-b px-1.5 py-1.5">
                    <DockTab
                        active={tab === "return"}
                        onClick={() => setTab("return")}
                        icon={<RotateCcw className="h-3.5 w-3.5" />}
                        label="Return needed"
                        count={returnCount}
                        badgeClass="bg-orange-700 text-white"
                    />
                    <DockTab
                        active={tab === "awaiting"}
                        onClick={() => setTab("awaiting")}
                        icon={<Inbox className="h-3.5 w-3.5" />}
                        label="Awaiting"
                        count={awaitingCount}
                        badgeClass="bg-amber-700 text-white"
                    />
                    <button
                        type="button"
                        title="Collapse queues"
                        aria-label="Collapse queues"
                        onClick={() => setCollapsed(true)}
                        className="ml-auto flex h-7 w-7 items-center justify-center rounded-md text-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
                    >
                        <ChevronsLeft className="h-4 w-4" />
                    </button>
                </div>
                <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-2">
                    {tab === "return" ? (
                        <ReturnNeededList
                            byJob={byJob}
                            onOpenJob={(jobId) => onJobClick?.(jobId)}
                            onReschedule={onRescheduleReturn}
                            onBackToQueue={(jobId) => backToQueue.mutate(jobId)}
                            backToQueuePending={backToQueue.isPending}
                            onDismiss={(jobFlags) => jobFlags.forEach((f) => dismiss.mutate(f.id))}
                            dismissPending={dismiss.isPending}
                        />
                    ) : (
                        <>
                            <CoverageQueueSection
                                onJobClick={onJobClick}
                                onJobDragStart={onJobDragStart}
                                onJobDragEnd={onJobDragEnd}
                            />
                            <BacklogPanel
                                techs={techs}
                                showReadiness={showReadiness}
                                targetDate={targetDate}
                                collapsed={backlogCollapsed}
                                techSkills={techSkills}
                                jobSkillReqs={jobSkillReqs}
                                techCityCerts={techCityCerts}
                                jobCityRestriction={jobCityRestriction}
                                onJobClick={onJobClick}
                                onScheduleJob={onScheduleJob}
                                onJobDragStart={onJobDragStart}
                                onJobDragEnd={onJobDragEnd}
                                onUnscheduleJob={onUnscheduleJob}
                                onCancelJob={onCancelJob}
                                onAssignJob={onAssignJob}
                            />
                        </>
                    )}
                </div>
            </div>
        </aside>
    );
}

function DockTab({
    active,
    onClick,
    icon,
    label,
    count,
    badgeClass,
}: {
    active: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    label: string;
    count: number;
    badgeClass: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            className={cn(
                "flex items-center gap-1.5 rounded-t-md border-b-[3px] px-2.5 py-1.5 text-xs transition-colors",
                // G6.1 polish (Corbin: "hard to tell which tab you are on") —
                // the active tab must be unmistakable at a glance: gold
                // underline + tinted fill + full-strength bold label; the
                // inactive tab clearly muted with no fill. amber-500 is a
                // fixed color that reads on the card surface in BOTH themes,
                // and text-foreground is the card's own always-correct text
                // token — nothing here flips underneath us (same doctrine as
                // the command bar's DARK_CTRL fix).
                active
                    ? "border-b-amber-500 bg-amber-500/15 font-bold text-foreground"
                    : "border-b-transparent font-semibold text-foreground/50 hover:bg-muted/60 hover:text-foreground/80",
            )}
        >
            {icon}
            <span>{label}</span>
            {count > 0 && (
                <span
                    className={cn("rounded-full px-1.5 py-px text-[10px] font-bold leading-3 tabular-nums", badgeClass)}
                >
                    {badgeText(count)}
                </span>
            )}
        </button>
    );
}

/**
 * Return-needed cards (G6.1 polish — Corbin: "too basic, and we should be
 * able to click into the job"). Each card is CLICKABLE — the body opens the
 * job detail page (same same-tab navigation the rest of the dock uses) so
 * the office can see exactly what happened and what's left to finish, or
 * decide to split/close it there. On the card: customer · site, the reason
 * chip(s), the tech's note (2-line clamp, full text on tooltip), the
 * device summary, and a right-chevron affordance. The three exits from the
 * old strip survive as icon buttons (stopPropagation so they never
 * navigate). All data comes from the existing return-needed payload — no
 * new endpoints. The bigger return-triage drawer is a future prompt.
 */
function ReturnNeededList({
    byJob,
    onOpenJob,
    onReschedule,
    onBackToQueue,
    backToQueuePending,
    onDismiss,
    dismissPending,
}: {
    byJob: Map<string, ReturnNeededFlag[]>;
    onOpenJob: (jobId: string) => void;
    onReschedule: (job: DispatchJob) => void;
    onBackToQueue: (jobId: string) => void;
    backToQueuePending: boolean;
    onDismiss: (jobFlags: ReturnNeededFlag[]) => void;
    dismissPending: boolean;
}) {
    if (byJob.size === 0) {
        return (
            <div className="flex flex-col items-center gap-1 py-8 text-center" data-testid="return-needed-empty">
                <RotateCcw className="h-6 w-6 text-foreground/30" />
                <p className="text-sm font-medium text-foreground/70">No return trips waiting.</p>
            </div>
        );
    }
    return (
        <div className="space-y-1.5" data-testid="return-needed-strip">
            {[...byJob.entries()].map(([jobId, jobFlags]) => {
                const first = jobFlags[0]!;
                const deviceBits = jobFlags
                    .filter((f) => f.asset_id)
                    .map((f) =>
                        [f.asset_name || "Device", f.asset_serial ? `S/N ${f.asset_serial}` : null]
                            .filter(Boolean)
                            .join(" "),
                    );
                const reasons = [...new Set(jobFlags.map((f) => REASON_LABEL[f.reason_category] ?? f.reason_category))];
                const detail = jobFlags
                    .map((f) => f.reason_text)
                    .filter(Boolean)
                    .join("; ");
                const noteTooltip = [detail ? `“${detail}”` : null, first.flagged_by_name].filter(Boolean).join(" — ");
                return (
                    <div
                        key={jobId}
                        role="button"
                        tabIndex={0}
                        onClick={() => onOpenJob(first.job_id)}
                        onKeyDown={(e) => {
                            // Only the card itself — Enter on an inner action
                            // button must not also open the job.
                            if (e.target !== e.currentTarget) return;
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onOpenJob(first.job_id);
                            }
                        }}
                        title="Open the job — see what happened and what's left to complete it"
                        className="group cursor-pointer space-y-1 rounded-md border border-orange-300/60 bg-background px-2 py-1.5 text-xs transition-all duration-150 hover:-translate-y-px hover:border-primary/50 hover:shadow-md"
                    >
                        {/* Line 1 — customer · site, job # and the click-through chevron */}
                        <div className="flex items-center gap-1.5">
                            <span className="min-w-0 flex-1 truncate">
                                <span className="font-semibold">{first.customer_name || first.job_title || "Job"}</span>
                                {(first.site_name || first.site_city) && (
                                    <span className="text-foreground/60">
                                        {" · "}
                                        {[first.site_name, first.site_city].filter(Boolean).join(" · ")}
                                    </span>
                                )}
                            </span>
                            {first.display_number && (
                                <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                                    #{first.display_number}
                                </span>
                            )}
                            <ChevronRight
                                aria-hidden="true"
                                className="h-3.5 w-3.5 shrink-0 text-foreground/40 transition-colors group-hover:text-foreground/80"
                            />
                        </div>
                        {/* Line 2 — reason chip(s) + affected devices */}
                        <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
                            {reasons.map((r) => (
                                <span
                                    key={r}
                                    className="shrink-0 rounded-full bg-orange-100 px-1.5 py-px text-[10px] font-medium text-orange-800 dark:bg-orange-900/50 dark:text-orange-200"
                                >
                                    {r}
                                </span>
                            ))}
                            {deviceBits.length > 0 && (
                                <span
                                    className="min-w-0 truncate text-[10px] text-foreground/70"
                                    title={deviceBits.join(", ")}
                                >
                                    {deviceBits.join(", ")}
                                </span>
                            )}
                        </div>
                        {/* Line 3 — the tech's note: what exactly happened out there */}
                        {detail && (
                            <p
                                className="line-clamp-2 text-[10px] italic leading-snug text-foreground/70"
                                title={noteTooltip}
                            >
                                “{detail}”
                                {first.flagged_by_name && (
                                    <span className="not-italic text-muted-foreground"> — {first.flagged_by_name}</span>
                                )}
                            </p>
                        )}
                        {/* Actions — the same three exits as the old strip; clicks
                            stay on the buttons (never navigate). */}
                        <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-primary"
                                title="Pick a time now (opens Schedule & Crew)"
                                aria-label="Pick a time now"
                                onClick={() =>
                                    onReschedule({
                                        id: first.job_id,
                                        title: first.job_title ?? "Job",
                                        status: first.job_status,
                                        customer_name: first.customer_name,
                                        assigned_to: first.assigned_to,
                                        assigned_to_name: null,
                                        trade_name: null,
                                        trade_code: null,
                                        scheduled_start: first.scheduled_start,
                                        scheduled_end: null,
                                        site_label: first.site_name,
                                        site_city: first.site_city,
                                    } as DispatchJob)
                                }
                            >
                                <CalendarPlus className="h-4 w-4" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                title="Back to Awaiting Dispatch — clear the time + tech and park the return trip in the queue with its reason and remaining devices"
                                aria-label="Back to Awaiting Dispatch"
                                disabled={backToQueuePending}
                                onClick={() => onBackToQueue(jobId)}
                            >
                                <Inbox className="h-4 w-4" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-foreground/60"
                                title="Dismiss — logged, doesn't delete the job (the return trip is handled another way)"
                                aria-label="Dismiss — logged, doesn't delete the job"
                                disabled={dismissPending}
                                onClick={() => onDismiss(jobFlags)}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
